import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  BookOpen,
  Mic,
  ImageIcon,
  Users,
  Clock,
  FileText,
  Headphones,
  Sparkles,
  Loader2,
  ChevronRight,
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { getApiBase } from '../../utils/apiBase';
import { treeHref, personHref } from '../../utils/prettyArchiveLinks';
import { buildTreePeopleAddPersonUrl } from '../../utils/archiveNavigation';
import { StoryPromptCard } from './StoryPromptCard';
import { StoryGuidePanel } from '../stories/StoryGuidePanel';
import { UsageLimitsPanel } from '../billing/UsageLimitsPanel';
import { SoftUpgradePrompt } from '../billing/SoftUpgradePrompt';
import { useUsageLimits } from '../../hooks/useUsageLimits';
import { shouldShowPdfExportPrompt } from '../../utils/usageLimits';
import { useExportEntitlements } from '../../hooks/useExportEntitlements';
import {
  exportActionIsLink,
  getExportButtonLabel,
  getExportHelperCopy,
  shouldShowExportUpgradeCopy,
} from '../../utils/exportEntitlements';
import { CORE_JOURNEY_UI, LEGACY_BOOK_HTML_PREVIEW } from '../../config/testerRelease';
import { CORE_EMOTIONAL_LINES } from '../../content/coreJourneyContent';
import { treeLegacyBookPreviewHref } from '../../utils/legacyBookPreview';

const API = getApiBase();

function ProgressRing({ percent, size = 88, className }) {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.min(100, Math.max(0, percent)) / 100) * c;
  return (
    <div className={cn('relative inline-flex items-center justify-center', className)}>
      <svg width={size} height={size} className="-rotate-90" aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="hsl(38 16% 88%)"
          strokeWidth={6}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="hsl(43 48% 58%)"
          strokeWidth={6}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          className="transition-[stroke-dashoffset] duration-700 ease-out"
        />
      </svg>
      <span className="absolute font-serif text-lg text-[hsl(24_14%_18%)] tabular-nums">{percent}%</span>
    </div>
  );
}

function MetricChip({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-xl border border-[hsl(38_16%_86%)]/80 bg-[hsl(42_28%_99%)]/90 px-3 py-2.5">
      <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.12em] text-[hsl(32_10%_42%)]">
        <Icon className="h-3.5 w-3.5 text-[#8b7355]" aria-hidden />
        {label}
      </div>
      <p className="mt-1 font-serif text-lg text-[hsl(24_14%_18%)]">{value}</p>
      {sub ? <p className="text-[11px] text-muted-foreground">{sub}</p> : null}
    </div>
  );
}

function ExportReadinessRow({ item, onExportPdf, onOpenAudiobook, exportEntitlement = null }) {
  const icons = {
    pdf: FileText,
    audiobook: Headphones,
    published_memory: Sparkles,
    book_title: BookOpen,
  };
  const Icon = icons[item.id] || BookOpen;
  const statusStyles = {
    ready: 'border-[hsl(88_18%_78%)]/90 bg-[hsl(88_24%_96%)]/80 text-[hsl(88_18%_28%)]',
    emerging: 'border-[hsl(43_48%_58%_/_0.35)] bg-[hsl(42_28%_99%)] text-[hsl(32_10%_38%)]',
    locked: 'border-[hsl(38_14%_88%)] bg-[hsl(42_22%_97%)]/80 text-muted-foreground',
  };
  const statusLabel = { ready: 'Ready', emerging: 'Taking shape', locked: 'Not yet' };

  return (
    <div
      className="flex items-start gap-3 rounded-xl border border-[hsl(38_14%_88%)]/60 bg-white/50 px-3 py-2.5"
      data-testid={`export-readiness-${item.id}`}
    >
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[hsl(42_28%_96%)] text-[#8b7355]">
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-serif text-sm text-[hsl(24_14%_18%)]">{item.label}</p>
          <span
            className={cn(
              'rounded-full border px-2 py-0.5 text-[10px] font-medium',
              statusStyles[item.status] || statusStyles.locked
            )}
          >
            {statusLabel[item.status] || item.status}
          </span>
        </div>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{item.hint}</p>
        {item.id === 'pdf' && shouldShowExportUpgradeCopy(exportEntitlement) ? (
          <p className="mt-1 text-[11px] leading-relaxed text-[#6c6258]">
            {getExportHelperCopy(exportEntitlement)}
          </p>
        ) : null}
      </div>
      {item.id === 'pdf' && item.status === 'ready' && exportActionIsLink(exportEntitlement) ? (
        <Button asChild type="button" size="sm" variant="outline" className="shrink-0 rounded-full text-xs">
          <Link to="/pricing#legacy-book">{getExportButtonLabel(exportEntitlement)}</Link>
        </Button>
      ) : null}
      {item.id === 'pdf' && item.status === 'ready' && onExportPdf && !exportActionIsLink(exportEntitlement) ? (
        <Button type="button" size="sm" variant="outline" className="shrink-0 rounded-full text-xs" onClick={onExportPdf}>
          {getExportButtonLabel(exportEntitlement)}
        </Button>
      ) : null}
      {item.id === 'audiobook' && (item.status === 'ready' || item.status === 'emerging') && onOpenAudiobook ? (
        <Button type="button" size="sm" variant="outline" className="shrink-0 rounded-full text-xs" onClick={onOpenAudiobook}>
          Listen
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Warm book-journey panel: progress metrics, chapter blocks, export readiness, prompt queue.
 */
export function BookProgressPanel({
  treeId,
  tree,
  className,
  onExportPdf,
  onExportPdfV2,
  exportingPdfV2 = false,
  playwrightExportPhase = null,
  compact = false,
}) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { snapshot: usageSnapshot } = useUsageLimits({ treeId, enabled: Boolean(treeId) });
  const { entitlement: exportEntitlement } = useExportEntitlements({ treeId, enabled: Boolean(treeId) });

  const fetchProgress = useCallback(async () => {
    if (!treeId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/trees/${treeId}/book-progress`);
      setData(res.data);
    } catch (e) {
      setError('Unable to load book progress right now.');
    } finally {
      setLoading(false);
    }
  }, [treeId]);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  const handlePromptAction = async (action, promptId) => {
    await axios.post(`${API}/trees/${treeId}/prompt-queue/action`, {
      action,
      prompt_id: promptId,
    });
    await fetchProgress();
  };

  const handleOpenAudiobook = () => {
    const base = tree ? treeHref(tree) : `/tree/${treeId}`;
    navigate(`${base}?tab=audiobook`);
  };

  const handleApplyChapterOrder = () => {
    handleOpenAudiobook();
  };

  const handleNextAction = () => {
    if (!data?.next_action) return;
    const a = data.next_action;
    const base = tree ? treeHref(tree) : `/tree/${treeId}`;
    switch (a.kind) {
      case 'add_person':
        navigate(buildTreePeopleAddPersonUrl(treeId));
        break;
      case 'record_story':
      case 'complete_chapter':
        if (a.person_id) {
          navigate(`${personHref({ id: a.person_id })}?view=stories`);
        } else {
          navigate(`${base}?tab=grid`);
        }
        break;
      case 'add_photo':
        if (a.person_id && a.story_id) {
          navigate(`${personHref({ id: a.person_id })}?view=stories&story=${a.story_id}`);
        }
        break;
      case 'add_connection':
        navigate(`${base}?tab=grid`);
        break;
      case 'timeline':
        navigate(`${base}?tab=timeline`);
        break;
      case 'book_title':
        navigate(base);
        break;
      case 'export_pdf':
        if (onExportPdf) onExportPdf();
        break;
      default:
        document.getElementById('story-prompt-card')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  if (loading) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-2xl border border-[hsl(38_16%_86%)] bg-[hsl(42_28%_99%)] py-12',
          className
        )}
        data-testid="book-progress-loading"
      >
        <Loader2 className="h-6 w-6 animate-spin text-[#8b7355]" aria-label="Loading book progress" />
      </div>
    );
  }

  if (error || !data) {
    return null;
  }

  const m = data.metrics || {};

  return (
    <section
      className={cn(
        'overflow-hidden rounded-2xl border border-[hsl(38_16%_82%)] bg-gradient-to-br from-[hsl(42_30%_100%)] via-[hsl(40_24%_98%)] to-[hsl(38_18%_94%)] shadow-[0_8px_32px_-16px_hsl(28_14%_18%_/_0.12)]',
        className
      )}
      data-testid="book-progress-panel"
      aria-label="Your book progress"
    >
      <div className={cn('p-5 sm:p-6', compact && 'sm:p-5')}>
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-[hsl(32_10%_42%)]">
              {CORE_JOURNEY_UI && compact ? 'More detail' : 'Your book progress'}
            </p>
            <h2 className="mt-1 font-serif text-xl font-normal text-[hsl(24_14%_16%)] sm:text-2xl">
              {tree?.name ? `${tree.name}` : 'Your family archive'}
            </h2>
            <p className="mt-2 max-w-lg text-sm leading-relaxed text-[hsl(32_10%_38%)]">
              {CORE_JOURNEY_UI && compact
                ? CORE_EMOTIONAL_LINES.growsOverTime
                : 'Each memory, voice note, and connection brings your keepsake closer — at your own pace.'}
            </p>
          </div>
          <ProgressRing percent={data.overall_percent} className="shrink-0 self-center sm:self-start" />
        </div>

        {LEGACY_BOOK_HTML_PREVIEW && treeId ? (
          <div className="mt-4 flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm" className="rounded-full">
              <Link to={treeLegacyBookPreviewHref(treeId)} data-testid="book-html-preview-link">
                Preview keepsake book
                <ChevronRight className="ml-1 h-4 w-4" aria-hidden />
              </Link>
            </Button>
          </div>
        ) : null}

        {data.next_action ? (
          <div
            className="mt-5 rounded-xl border border-[#c9a961]/30 bg-[#fffaf3]/80 p-4 sm:flex sm:items-center sm:justify-between sm:gap-4"
            data-testid="book-next-action"
          >
            <div className="min-w-0">
              <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-[#8b7355]">Suggested next step</p>
              <p className="mt-1 font-serif text-base text-[hsl(24_14%_18%)]">{data.next_action.title}</p>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{data.next_action.body}</p>
            </div>
            <Button
              type="button"
              className="mt-3 shrink-0 rounded-full sm:mt-0"
              onClick={handleNextAction}
              data-testid="book-next-action-cta"
            >
              {data.next_action.cta_label}
              <ChevronRight className="ml-1 h-4 w-4" aria-hidden />
            </Button>
          </div>
        ) : null}

        <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          <MetricChip
            icon={BookOpen}
            label="Chapters"
            value={`${m.chapters_complete ?? 0}/${m.chapters_total ?? 4}`}
          />
          <MetricChip icon={BookOpen} label="Memories" value={m.stories_with_content ?? 0} sub="with words or voice" />
          <MetricChip icon={Mic} label="Voice" value={m.voice_stories ?? 0} />
          <MetricChip icon={ImageIcon} label="Photos" value={m.photos_attached ?? 0} />
          <MetricChip icon={Users} label="Connections" value={m.connections ?? 0} />
          <MetricChip
            icon={Clock}
            label="Timeline"
            value={`${m.timeline_coverage_percent ?? 0}%`}
            sub="dates placed"
          />
        </div>

        <div className="mt-5">
          <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(32_10%_42%)]">
            Chapter completion
          </p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {(data.chapters || []).map((ch) => (
              <div
                key={ch.id}
                className={cn(
                  'rounded-xl border px-3 py-2.5 transition-colors',
                  ch.complete
                    ? 'border-[hsl(43_48%_58%_/_0.45)] bg-[hsl(42_28%_99%)]'
                    : 'border-dashed border-[hsl(38_16%_86%)] bg-white/40'
                )}
                data-testid={`book-chapter-${ch.id}`}
              >
                <p className="font-serif text-sm text-[hsl(24_14%_18%)]">{ch.title}</p>
                <p className="text-[11px] text-muted-foreground">{ch.subtitle}</p>
                <p className="mt-1 text-[10px] text-[hsl(32_10%_42%)]">
                  {ch.complete ? `${ch.story_count} ${ch.story_count === 1 ? 'memory' : 'memories'}` : 'Waiting'}
                </p>
              </div>
            ))}
          </div>
        </div>

        {treeId ? (
          <div className="mt-5">
            <StoryGuidePanel
              surface="book"
              treeId={treeId}
              onApplyChapterOrder={handleApplyChapterOrder}
            />
          </div>
        ) : null}

        <div className="mt-5">
          <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(32_10%_42%)]">
            Keepsake readiness
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {(data.export_readiness || []).map((item) => (
              <ExportReadinessRow
                key={item.id}
                item={item}
                onExportPdf={onExportPdf}
                onOpenAudiobook={handleOpenAudiobook}
                exportEntitlement={item.id === 'pdf' ? exportEntitlement : null}
              />
            ))}
          </div>
          {usageSnapshot && shouldShowPdfExportPrompt(usageSnapshot) ? (
            <SoftUpgradePrompt
              promptId="pdf-export"
              treeId={treeId}
              visible
              snapshot={usageSnapshot}
              message="Your Legacy Book preview is included. The finished keepsake export will become part of the Legacy Book founding offer."
              ctaLabel="Reserve founding offer"
              ctaTo="/pricing#legacy-book"
              featureName="legacy_book_pdf"
              className="mt-3"
              data-testid="soft-upgrade-prompt-pdf-export"
            />
          ) : null}
          {typeof onExportPdfV2 === 'function' ? (
            <div className="mt-3 rounded-xl border border-dashed border-[hsl(38_16%_82%)] bg-white/50 px-3 py-3">
              <p className="text-xs font-medium text-[hsl(24_14%_18%)]">Experimental HTML PDF</p>
              <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                Generates a keepsake PDF in the background via Playwright. The standard Legacy Book PDF remains the default.
              </p>
              {playwrightExportPhase && playwrightExportPhase !== 'ready' ? (
                <p className="mt-2 text-[11px] text-[hsl(32_10%_38%)]" data-testid="legacy-book-export-phase">
                  {playwrightExportPhase === 'queued' && 'Queued…'}
                  {playwrightExportPhase === 'generating' && 'Generating your PDF…'}
                  {playwrightExportPhase === 'starting' && 'Starting…'}
                  {playwrightExportPhase === 'failed' && 'Generation failed.'}
                  {!['queued', 'generating', 'starting', 'failed', 'ready'].includes(playwrightExportPhase)
                    ? `Status: ${playwrightExportPhase}`
                    : null}
                </p>
              ) : null}
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="mt-2 rounded-full text-xs"
                disabled={exportingPdfV2}
                onClick={onExportPdfV2}
                data-testid="legacy-book-export-pdf-v2"
              >
                {exportingPdfV2
                  ? playwrightExportPhase === 'generating'
                    ? 'Generating…'
                    : playwrightExportPhase === 'queued'
                      ? 'Queued…'
                      : 'Working…'
                  : 'Generate new PDF'}
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      {data.current_prompt ? (
        <div className="border-t border-[hsl(38_14%_88%)]/70 bg-[hsl(42_24%_98%)]/60 p-5 sm:p-6" id="story-prompt-card">
          <StoryPromptCard
            prompt={data.current_prompt}
            savedCount={data.prompt_saved_count}
            doneCount={data.prompt_done_count}
            onAction={handlePromptAction}
            treeId={treeId}
          />
        </div>
      ) : null}

      <div className="border-t border-[hsl(38_14%_88%)]/70 p-5 sm:p-6">
        <UsageLimitsPanel treeId={treeId} className="border-0 bg-transparent shadow-none" data-testid="readiness-usage-limits" />
      </div>
    </section>
  );
}

export default BookProgressPanel;
